/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React from 'react';
import PropTypes from 'prop-types';
import { gnoment } from 'shared-ui/gnoment';
import { connect } from 'react-redux';
import get from 'lodash/get';

import {
    Box,
    ContentBlock,
    Heading,
    ProgressIndicator,
    SecurityHelper,
    Spacer,
} from 'sarsaparilla';
import { withCompatRouter, COMPAT_ROUTER_PROP_TYPES } from 'sarsaparilla/legacy';

import {
    OrderDetailsLayout,
    OrderDetailsTimerNotice,
    OrderDetailsSection,
    OrderDetailsSummary,
} from 'shared-ui/components/OrderDetailsLayout';
import * as sharedActions from 'ui-permit/shared-components/actions/actions';

import ReservationDetail from '../components/registration/ReservationDetail';
import * as pActions from '../actions/permit';
import { issuanceStatuses } from '../utils/permitUtilsNew';
import * as types from '../constants';
import ValidationError from '../components/registration/ValidationError';
import CooperatorsList from '../components/CooperatorsList';
import { selectors as sharedSelectors } from '../reducers/shared';

const PROGRESS_STEPS = ['Order Details', 'Review Cart', 'Payment', 'Confirmation'];

export class SelectCooperatorPage extends React.Component {
    static propTypes = {
        router: COMPAT_ROUTER_PROP_TYPES,
        permit: PropTypes.object,
        issuance: PropTypes.object,
        issuanceList: PropTypes.array,
        dispatch: PropTypes.func,
        issuanceLoading: PropTypes.bool,
        imageUrl: PropTypes.string,
    };

    constructor(props) {
        super(props);
        this.state = {
            displayError: false,
            division: {},
            errors: [],
            selectedCooperatorId: null,
        };
        this.onCooperatorSelect = this.onCooperatorSelect.bind(this);
    }

    componentDidMount() {
        // fetch first issuance from the list of reservation ids and initialize
        // this page allows bulk editing of "initial" issuances (if there are multiple)
        const firstReservationId = this.props.router.params.reservationIds.split(',')[0];
        const issuanceId = firstReservationId.split(types.RES_ID_DELIMITER)[0];
        const permitId = types.BOUNDARY_WATERS_PERMIT_ID;
        this.props.dispatch(sharedActions.fetchReservationSummaryImageUrl(permitId));
        this.props.dispatch(pActions.fetchCart());
        Promise.all([
            this.props.dispatch(pActions.fetchUnpaidIssuance(issuanceId, permitId)),
            this.props.dispatch(pActions.fetchPermit(permitId)),
        ]).then(() => {
            let recId = 0;
            if (this.props.permit.recareas && this.props.permit.recareas[0]) {
                recId = this.props.permit.recareas[0];
                this.props.dispatch(pActions.fetchRecarea(recId));
            }
            if (!this.props.issuance.division_id) {
                const initDivision = this.getDivision();
                this.setState({ division: initDivision });
            } else {
                this.setState({
                    division:
                        this.props.permit.divisions[this.props.issuance.division_id],
                });
            }
            this.setState({
                selectedCooperatorId: this.props.issuance.cooperator_id,
            });
        });

        const issuanceIdList = this.props.router.params.reservationIds
            .split(',')
            .map((reservationId) => reservationId.split(types.RES_ID_DELIMITER)[0]);
        if (issuanceIdList.length > 1) {
            this.props.dispatch(
                pActions.fetchUnpaidIssuanceList(issuanceIdList, permitId)
            );
        } else {
            this.props.dispatch(pActions.clearUnpaidIssuanceList());
        }
    }

    onCooperatorSelect(cooperator) {
        this.setState({ selectedCooperatorId: cooperator.cooperator_id });
    }

    isThisFormValid() {
        if (!this.state.selectedCooperatorId) {
            this.setState({
                errors: ['Please select an issuing station for your permit'],
            });
            return false;
        }
        return true;
    }

    getDivision = () => {
        let divisionInIssuance;
        if (this.props.issuance.division_id) {
            divisionInIssuance =
                this.props.permit.divisions[this.props.issuance.division_id];
        }
        return divisionInIssuance;
    };

    continue = async () => {
        if (!this.isThisFormValid()) {
            this.setState({ displayError: true });
            return;
        }

        const permitId = types.BOUNDARY_WATERS_PERMIT_ID;

        this.props.dispatch({
            type: types.SET_ISSUANCE_LOADING,
            issuanceLoading: true,
        });
        try {
            if (get(this.props, 'issuanceList.length')) {
                await pActions.updateItems(
                    this.props.issuanceList.map((issuance) => ({
                        ...issuance,
                        cooperator_id: this.state.selectedCooperatorId,
                    }))
                );
            } else {
                await pActions.updateItems([
                    {
                        ...this.props.issuance,
                        cooperator_id: this.state.selectedCooperatorId,
                    },
                ]);
            }
        } catch (error) {
            this.setState({ displayError: true, errors: ['There were errors saving'] });
            return;
        } finally {
            this.props.dispatch({
                type: types.SET_ISSUANCE_LOADING,
                issuanceLoading: false,
            });
        }
        const redirectURL = `/permits/${permitId}/registration/${this.props.router.params.reservationIds}`;
        this.props.router.navigate(redirectURL);
    };

    isLoading = () => {
        const firstReservationId = this.props.router.params.reservationIds.split(',')[0];
        return (
            !this.props.permit?.id ||
            !this.props.issuance?.id ||
            !this.state.division.name ||
            firstReservationId !== `${this.props.issuance.id}__${this.props.permit.id}`
        );
    };

    renderTimeNotice = () => {
        // Permit complete does not have time limit to fill order details
        if (this.props.issuance.status === issuanceStatuses.get(types.PERMIT_COMPLETE)) {
            return null;
        }
        return <OrderDetailsTimerNotice />;
    };

    renderSummary() {
        const isLoading = this.isLoading();
        const disabledButtons =
            this.props.issuanceLoading ||
            this.props.issuance.status === issuanceStatuses.get(types.PERMIT_ISSUED);
        return (
            <OrderDetailsSummary
                heading={`${this.state.division?.name || ''} (${SecurityHelper.isCommercialAccount() ? 'Commercial' : 'Non-Commercial'})`}
                imageUrl={this.props.imageUrl}
                isLoading={isLoading}
                isWaitingForServerResponse={this.props.issuanceLoading}
                parentName={this.props.permit.name}
                isProceedToCartDisabled={disabledButtons}
                onProceedToCartClick={this.continue}
                proceedToCartButtonText="Continue"
            >
                <ReservationDetail
                    isPreview
                    permit={this.props.permit}
                    issuanceList={this.props.issuanceList}
                    division={this.state.division}
                    entryDate={gnoment(this.props.issuance.start_date).utc()}
                    exitDate={
                        this.props.issuance.end_date
                            ? gnoment(this.props.issuance.end_date).utc()
                            : null
                    }
                    issuanceStatus={this.props.issuance.status}
                />
            </OrderDetailsSummary>
        );
    }

    render() {
        const isLoading = this.isLoading();

        return (
            <div className="boundarywaters-select-cooperator-page">
                <Box background="gray-2" paddingY="sm">
                    <ProgressIndicator steps={PROGRESS_STEPS} activeStep={1} />
                </Box>
                <Spacer size="xl" />
                <ContentBlock>
                    <OrderDetailsLayout summaryElement={this.renderSummary()}>
                        <Heading headingLevel={1} appearance="h3">
                            Order Details
                        </Heading>
                        {this.renderTimeNotice()}

                        <ValidationError
                            displayError={this.state.displayError}
                            toggle={() => this.setState({ displayError: false })}
                        >
                            {this.state.errors}
                        </ValidationError>
                        <OrderDetailsSection
                            heading={this.props.permit.name}
                            isLoading={isLoading}
                        >
                            <Heading headingLevel={2} appearance="h5">
                                Select Permit Issue Station
                                <abbr className="rec-label-required-indicator">
                                    <span aria-hidden="true">*</span>
                                    <span className="rec-sr-only"> (Required) </span>
                                </abbr>
                            </Heading>
                            <CooperatorsList
                                isSelectable
                                selectedCooperatorId={this.props.issuance.cooperator_id}
                                onSelect={this.onCooperatorSelect}
                            />
                        </OrderDetailsSection>
                    </OrderDetailsLayout>
                </ContentBlock>
            </div>
        );
    }
}

function select(state) {
    return {
        permit: state.boundarywaters.permit.result,
        imageUrl: sharedSelectors.getImageUrlSelector(state),
        issuance: state.boundarywaters.permit.issuance,
        issuanceList: state.boundarywaters.permit.issuanceList,
        issuanceError: state.boundarywaters.permit.error,
        recareaError: state.boundarywaters.permit.recarea_err,
        fetchError: state.boundarywaters.permit.fetch_err,
        cart: state.boundarywaters.permit.cart,
        cartError: state.boundarywaters.permit.cart_err,
        filters: state.boundarywaters.permit.filters,
        issuanceLoading: state.boundarywaters.issuanceLoading,
    };
}

export default withCompatRouter(connect(select)(SelectCooperatorPage));

// cSpell:ignore boundarywaters, recarea, recareas
